import { CSSProperties, useMemo, useState, type UIEvent } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { OrganizationSuggestions } from "@entities/company/ui";
import { ProjectParticipantInfo, projectQueries } from "@entities/project";
import { DropdownLoader } from "./dropdown-loader";

interface ParticipantsInfiniteScrollProps {
  projectId: string;
  onPartySelect: (company: ProjectParticipantInfo) => void;
  onPartyClear?: () => void;
  style?: CSSProperties;
  isLoading?: boolean;
  value?: string;
  defaultValue?: string;
}

export function ParticipantsInfiniteScroll({
  projectId,
  onPartySelect,
  onPartyClear,
  style,
  isLoading,
  value,
  defaultValue,
}: ParticipantsInfiniteScrollProps) {
  const [searchValue, setSearchValue] = useState("");
  const {
    data: response,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...projectQueries.getProjectParticipantsInfinite({
      projectId,
      limit: 10,
      search: searchValue,
    }),
  });

  const parties = useMemo(() => {
    if (response?.pages) {
      return response.pages.flatMap((page) => page.participants);
    }
    return [];
  }, [response?.pages.length, searchValue]);

  const onPopupScroll = async (e: UIEvent<HTMLDivElement>) => {
    const element = e.target as HTMLDivElement;
    if (element.scrollTop + element.offsetHeight >= element.scrollHeight - 60) {
      await fetchNextPage();
    }
  };

  const onClear = () => {
    setSearchValue("");
    onPartyClear?.();
  };

  return (
    <OrganizationSuggestions
      defaultValue={defaultValue}
      style={style}
      popupMatchSelectWidth={false}
      placement="bottomLeft"
      parties={parties}
      loading={isLoading}
      onPartySelect={onPartySelect}
      onSearch={setSearchValue}
      value={value}
      onPopupScroll={onPopupScroll}
      onClear={onClear}
      dropdownRender={(menu) =>
        DropdownLoader({
          menu,
          isLoading: isFetchingNextPage,
        })
      }
    />
  );
}
